<template>
    <v-container>
        <v-row justify="center">
            <v-col cols="12" sm="12" md="12">
                <v-card class="pa-4" elevation="4">
                    <v-card-title>
                        <h1 class="text-h5 font-weight-bold text-grey-darken-4">
                            {{ isEditing ? 'Editar Cupom' : 'Novo Cupom' }}
                        </h1>
                    </v-card-title>
                    <v-card-text>

                        <v-form ref="formRef" @submit.prevent="handleSubmit">
                            <v-row class="mt-2">

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="formValue.code" label="Código do Cupom" :rules="[rules.code, rules.noSpaces]"
                                        required :disabled="isEditing && isSubmitting"></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-select v-model="formValue.type" :items="typeOptions" item-title="label"
                                        item-value="value" label="Tipo de Desconto" :rules="[rules.type]"
                                        required></v-select>
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="valueFormatted" :label="valueLabel" :rules="[rules.value]"
                                        required @input="formatInput" :prefix="formValue.type === 'fixed' ? 'R$' : '%'"
                                        :suffix="formValue.type === 'percent' ? '%' : ''"></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="formValue.usage_limit" label="Limite de Usos"
                                        type="number" min="1" clearable></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="formValue.expires_at"
                                        label="Expira em" type="date" clearable></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="3" v-if="isEditing">
                                    <v-text-field :model-value="formValue.usage_count" label="Usos Atuais"
                                        disabled></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="6" md="3">
                                    <v-switch v-model="formValue.is_active" color="success"
                                        label="Cupom Ativo?"></v-switch>
                                </v-col>

                            </v-row>
                        </v-form>

                    </v-card-text>

                    <v-card-actions class="d-flex justify-space-between mt-4">
                        <v-btn color="secondary" variant="flat" @click="handleCancel">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar para Lista
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                            @click="handleSubmit">
                            <v-icon left>mdi-content-save</v-icon>
                            {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Cupom' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingStore } from '@/store/loadingStore';

const toastStore = useToastStore();
const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const isSubmitting = ref(false);

const formValue = ref({
    code: '',
    type: 'fixed', // Padrão
    value: 0.00,
    usage_limit: null,
    usage_count: 0,
    is_active: true,
    expires_at: null,
});

const valueFormatted = ref('');
const isEditing = computed(() => !!route.params.id);

const typeOptions = [
    { label: 'Valor Fixo (R$)', value: 'fixed' },
    { label: 'Porcentagem (%)', value: 'percent' },
];

const valueLabel = computed(() =>
    formValue.value.type === 'percent' ? 'Valor (%)' : 'Valor (R$)'
);

const rules = {
    code: v => !!v || 'O código é obrigatório.',
    noSpaces: v => (v && !/\s/.test(v)) || 'O código não pode conter espaços.',
    type: v => !!v || 'O tipo de desconto é obrigatório.',
    value: () => (formValue.value.value !== null && formValue.value.value !== undefined && formValue.value.value > 0) || 'O valor é obrigatório e deve ser maior que zero.',
};

const loadCoupon = async (id) => {
    loadingStore.show();
    try {
        const response = await api.get(`/coupons/${id}`);
        const data = response.data.data;

        if (data) {
            formValue.value = {
                ...data,
                value: parseFloat(data.value),
                usage_limit: data.usage_limit ? parseInt(data.usage_limit) : null,
                usage_count: parseInt(data.usage_count),
                is_active: data.is_active === 1,
                // Formata a data para o input type="date" (AAAA-MM-DD)
                expires_at: data.expires_at ? new Date(data.expires_at).toISOString().split('T')[0] : null,
            };

            valueFormatted.value = formValue.value.value
        ? new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(formValue.value.value)
        : "";

            await nextTick();
            if (formRef.value) {
                formRef.value.resetValidation();
            }
        } else {
            toastStore.showToast({
                message: 'O cupom não foi encontrado.',
                color: 'warning',
            });
        }
    } catch (error) {
        console.error('Erro ao carregar dados do cupom:', error);
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao carregar dados do cupom.',
            color: 'error',
        });
    } finally {
        loadingStore.hide();
    }
};

const handleSubmit = async () => {
    const { valid } = await formRef.value.validate();
    if (!valid) {
        return;
    }

    isSubmitting.value = true;

    // Prepara o payload
    const payload = {
        code: formValue.value.code.toUpperCase(), // Garante CAIXA ALTA no envio
        type: formValue.value.type,
        value: formValue.value.value,
        usage_limit: formValue.value.usage_limit || null,
        is_active: formValue.value.is_active ? 1 : 0,
        expires_at: formValue.value.expires_at || null,
    };

    try {
        if (isEditing.value) {
            await api.put(`/coupons/${route.params.id}`, payload);
        } else {
            await api.post('/coupons', payload);
        }

        router.push('/app/cupons');
        toastStore.showToast({
            message: isEditing.value ? 'Cupom atualizado com sucesso!' : 'Cupom cadastrado com sucesso!',
            color: 'success',
        });

    } catch (error) {
        isSubmitting.value = false;
        console.error('Erro ao salvar cupom:', error);
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao salvar cupom.',
            color: 'error',
        });
    }
};

const handleCancel = () => {
    router.push('/app/cupons');
};

const formatInput = (event) => {
    // Mantém a limpeza e conversão para float / 100
    const clean = event.target.value.replace(/[^\d]/g, ""); 
    const num = parseInt(clean, 10) / 100;

    formValue.value.value = isNaN(num) ? null : num;

    // A formatação agora retorna apenas o número com vírgula/ponto, sem R$ ou %
    if (formValue.value.value !== null) {
        valueFormatted.value = formValue.value.value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true // Adiciona separador de milhar se necessário
        });
    } else {
        valueFormatted.value = "";
    }
};

watch(
    () => formValue.value.value,
    (newValue) => {
        if (newValue !== null && typeof newValue === 'number') {
            // A formatação agora retorna apenas o número com vírgula/ponto, sem R$ ou %
            valueFormatted.value = newValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true
            });
        } else {
            valueFormatted.value = "";
        }
    }, { immediate: true }
);

watch(() => formValue.value.type, () => {
    // Re-aciona o watcher de 'value' para garantir que a formatação (apenas numérica) seja aplicada
    if (formValue.value.value !== null) {
        const tempValue = formValue.value.value;
        formValue.value.value = undefined; // Força a detecção da mudança
        nextTick(() => {
            formValue.value.value = tempValue;
        });
    }
});

onMounted(() => {
    if (isEditing.value) {
        loadCoupon(route.params.id);
    }
});
</script>

<style scoped>
/* Estilos específicos se necessário */
</style>